import { CalendarToday, AccessTime } from '@mui/icons-material';
import { Grid, TextField } from '@mui/material';
import FormInputLabel from 'common/FormInputLabel';
import Subtext from 'common/Subtext';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import { offset } from '@floating-ui/react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styled from 'styled-components';

import { useAuth } from 'context/AuthContext';

import { useEffect, useState } from 'react';

dayjs.extend(isBetween);
dayjs.extend(weekday);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);

/** No flip middleware — calendar stays below the input (default react-datepicker flips upward when space is tight). */
const DATE_PICKER_POPPER_PROPS = {
    middleware: [offset(10)],
};

const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function generateAvailableHoursss(providerAvailability, slotsGap) {
    const availableHours = {
        Sunday: [],
        Monday: [],
        Tuesday: [],
        Wednesday: [],
        Thursday: [],
        Friday: [],
        Saturday: [],
    };

    // Handle cases where providerAvailability is null, undefined, or not an array
    if (!providerAvailability || !Array.isArray(providerAvailability) || providerAvailability.length === 0) {
        return availableHours;
    }

    // Handle invalid slotsGap
    if (!slotsGap || isNaN(slotsGap) || slotsGap <= 0) {
        slotsGap = 30; // Default to 30 minutes
    }

    try {
        providerAvailability.forEach(({ start_datetime, end_datetime, day }) => {
            if (!start_datetime || !end_datetime || day === undefined || day === null) {
                return;
            }

            const dayIndex = day % 7; // 1 = Monday → 1, 7 = Sunday → 0
            const baseDate = dayjs().weekday(dayIndex);
            const start = dayjs(`${baseDate.format('YYYY-MM-DD')}T${start_datetime}`);
            let end = dayjs(`${baseDate.format('YYYY-MM-DD')}T${end_datetime}`);

            if (end.isSameOrBefore(start)) {
                end = end.add(1, 'day');
            }

            let current = start;
            let iterations = 0; // Safety counter to prevent infinite loops
            const maxIterations = 1000; // Maximum number of slots to generate per day
            
            while (current.isBefore(end) && iterations < maxIterations) {
                const slot = current.format('HH:mm');
                const slotDay = WEEK_DAYS[current.day()];
                if (!availableHours[slotDay].includes(slot)) {
                    availableHours[slotDay].push(slot);
                }
                current = current.add(slotsGap, 'minute');
                iterations++;
            }
            
            if (iterations >= maxIterations) {
            }
        });
    } catch (error) {
        console.error('💥 Error:', error.message);
    }

    return availableHours;
}
const StyledDatePickerWrapper = styled.div`
    width: 100%;
    .react-datepicker-popper {
        left: 20px !important;
        z-index: 9999 !important; /* ensure calendar overlays chips or other UI */
    }
    .react-datepicker-wrapper {
        width: 100%;
    }
    .react-datepicker {
        z-index: 9999 !important;
    }

    .react-datepicker__input-container input {
        width: 100%;
        outline: none;
        font-size: 14px;
        border-radius: 8px;
        padding: 9.5px 42px 9.5px 20px;
        background: #f5f1ed;
        border: 1px solid #cbc1b7;
        color: #000000;
        font-family: 'Poppins', sans-serif;
        transition: all 0.3s ease;
        
        &::placeholder {
            color: #4A4A4A;
            opacity: 1;
        }
        
        &:hover {
            border-color: #ff5f39;
            background: white;
        }
        
        &:focus {
            border-color: #ff5f39;
            background: white;
            box-shadow: 0 0 0 2px rgba(255, 95, 57, 0.1);
        }
    }

    /* Disabled state to visually match MUI TextField disabled styling */
    .react-datepicker__input-container input:disabled {
        opacity: 0.6;
        color: #6B7280;
        background: #f5f1ed;
        border: 1px solid #cbc1b7;
        cursor: not-allowed;
    }

    /* Disable hover effects when the input is disabled */
    .react-datepicker__input-container input:disabled:hover,
    .react-datepicker__input-container input:disabled:focus {
        background: #f5f1ed !important;
        border: 1px solid #cbc1b7 !important;
        box-shadow: none !important;
    }
`;

const StyledDatePicker = (props) => (
    <StyledDatePickerWrapper>
        <DatePicker {...props} popperClassName="react-datepicker-popper" />
    </StyledDatePickerWrapper>
);

const statusColors = {
    // Appointment Status Colors
    booked: 'rgba(117, 189, 47, 1)', // Green for booked
    pending: 'rgba(149, 207, 211, 1)', // Teal for pending
    cancelled: 'rgba(180, 35, 24, 1)', // Red for cancelled
    cancelled_patient: 'rgba(180, 35, 24, 1)', // Red for cancelled by patient
    cancelled_provider: 'rgba(180, 35, 24, 1)', // Red for cancelled by provider

    // Payment Status Colors
    paid: 'rgba(117, 189, 47, 1)', // Green for paid
    unpaid: 'rgba(149, 207, 211, 1)', // Teal for unpaid
    pending: 'rgba(149, 207, 211, 1)', // Teal for pending
    declined: 'rgba(180, 35, 24, 1)', // Red for declined
    refunded: 'rgba(117, 189, 47, 1)', // Green for refunded
};

export default function FormTextInput({
    formData,
    label,
    name,
    value = null,
    handleChange,
    placeholder,
    InputProps,
    type = 'text',
    subText = '',
    subtext,
    helperText = '',
    labelSize = 6,
    textSize = 6,
    required = false,
    isTextFeild = true,
    noLabel = false,
    additionAction = null,
    disabled = false,
    readOnly = false,
    startDate,
    endDate,
    min,
    style,
    id,
    error,
    inputMode,
    pattern,
    onInput,
    title,
    inputProps,
    invoice = true,
    disableFiltering = false,
    bookedSlots,
    slotsGap,
    providerAvailabilityData,
    oooData,
    labelStyle = {},
}) {
    const { token } = useAuth();
    const [availability, setAvailability] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [oooDates, setOooDates] = useState(null);
    
    // Initialize selectedTime from value prop when it changes (for loading existing data)
    useEffect(() => {
        if (type === 'time' && value) {
            try {
                // Parse HH:MM format string to Date object
                const timeMatch = String(value).match(/(\d{1,2}):(\d{2})/);
                if (timeMatch) {
                    const hours = parseInt(timeMatch[1], 10);
                    const minutes = parseInt(timeMatch[2], 10);
                    const date = new Date(`1970-01-01T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`);
                    if (!isNaN(date.getTime())) {
                        // Only update if the value has actually changed
                        const currentTimeStr = selectedTime ? `${String(selectedTime.getHours()).padStart(2, '0')}:${String(selectedTime.getMinutes()).padStart(2, '0')}` : null;
                        if (currentTimeStr !== value) {
                            setSelectedTime(date);
                        }
                    }
                }
            } catch (error) {
                console.error('Error initializing time from value:', error);
            }
        } else if (type === 'time' && !value) {
            // Clear selectedTime if value is cleared
            setSelectedTime(null);
        }
    }, [value, type]);
    useEffect(() => {
        const fetchProviderAvailability = async () => {
            if (type !== 'time') return;
            if (!formData?.provider) return;
            if (typeof formData?.provider === 'string' && String(formData?.provider).startsWith('business_')) {
                setAvailability({ Sunday: [], Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] });
                return;
            }
            if (!slotsGap || isNaN(slotsGap) || slotsGap <= 0) return;

            try {
                // Use cached OOO data if provided by parent, otherwise fetch
                const outOfOfficeHours = Array.isArray(oooData)
                    ? oooData
                    : await getOooByProviderId(Number(formData?.provider));
                setOooDates(outOfOfficeHours);

                // Only fetch availability internally when parent is NOT managing it
                if (providerAvailabilityData === undefined) {
                    const result = await getProviderAvailablity(Number(formData?.provider));
                    const availableHours = generateAvailableHoursss(result?.providerAvailablity, slotsGap);
                    setAvailability(availableHours);
                }
            } catch (error) {
                console.error('💥 FormTextInput: Error fetching provider availability:', error);
                setAvailability({ Sunday: [], Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] });
            }
        };
        fetchProviderAvailability();
    }, [formData?.provider, slotsGap, type, oooData]);

    // When parent provides availability data, generate time slots from it
    useEffect(() => {
        if (providerAvailabilityData === undefined || providerAvailabilityData === null) return;
        if (!slotsGap || isNaN(slotsGap) || slotsGap <= 0) return;
        const availableHours = generateAvailableHoursss(providerAvailabilityData, slotsGap);
        setAvailability(availableHours);
    }, [providerAvailabilityData, slotsGap]);
    function generateAvailableHours(providerAvailability, outOfOfficeHours) {
        const availableHours = {
            Sunday: [],
            Monday: [],
            Tuesday: [],
            Wednesday: [],
            Thursday: [],
            Friday: [],
            Saturday: [],
        };

        // Use OOO actual date (no need to re-map)
        const formattedOOO = outOfOfficeHours.map(({ date_start, date_end }) => ({
            start: dayjs.utc(date_start),
            end: dayjs.utc(date_end),
        }));

        providerAvailability.forEach(({ start_datetime, end_datetime, day }) => {
            const dayIndex = parseInt(day) % 7;

            // 🧠 Now generate a baseDate that matches the date of OOO (not future week)
            // Find matching OOO date for this weekday
            const oooForDay = formattedOOO.find(({ start }) => start.day() === dayIndex);
            const baseDate = oooForDay ? dayjs.utc(oooForDay.start.format('YYYY-MM-DD')) : dayjs.utc().day(dayIndex);

            const start = dayjs.utc(`${baseDate.format('YYYY-MM-DD')}T${start_datetime}`);
            let end = dayjs.utc(`${baseDate.format('YYYY-MM-DD')}T${end_datetime}`);
            if (end.isSameOrBefore(start)) {
                end = end.add(1, 'day');
            }

            let current = start;
            while (current.isBefore(end)) {
                const slot = current.format('HH:mm');
                const slotDay = WEEK_DAYS[current.day()];

                const isOOO = formattedOOO.some(({ start, end }) => current.isBetween(start, end, null, '[)'));

                if (!isOOO && !availableHours[slotDay].includes(slot)) {
                    availableHours[slotDay].push(slot);
                }

                current = current.add(slotsGap, 'minute');
            }
        });

        return availableHours;
    }

    const handleDateChange = (date) => {
        try {
            const formattedDate = moment(date).format('DD MMMM, YYYY');
            setSelectedDay(date);
            localStorage.setItem('selectedDay', date);
            handleChange({ target: { name, value: formattedDate } });
        } catch (error) {
            console.error('Error handling date change:', error);
        }
    };

    // Helper function to safely parse date value and handle browser autofill formats
    const getSafeDateValue = (value) => {
        if (!value) return null;
        
        try {
            const dateStr = String(value).trim();
            
            // Handle YYYY-MM-DD format (HTML date input format)
            // CRITICAL: Parse as local date to avoid timezone shifts
            // new Date("2025-11-15") creates UTC midnight, which shifts to previous day in some timezones
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                // Parse YYYY-MM-DD as local date (not UTC) to preserve the date
                const [year, month, day] = dateStr.split('-').map(Number);
                const localDate = new Date(year, month - 1, day); // month is 0-indexed
                if (!isNaN(localDate.getTime())) {
                    return localDate;
                }
            }
            
            // Try to create a date object for other formats
            const date = new Date(value);
            
            // Check if the date is valid
            if (isNaN(date.getTime())) {
                // Try moment parsing as fallback
                const momentDate = moment(value);
                if (momentDate.isValid()) {
                    return momentDate.toDate();
                }
                
                // If all parsing fails, return null to avoid invalid date
                console.warn('Invalid date value:', value);
                return null;
            }
            
            return date;
        } catch (error) {
            console.error('Error parsing date value:', error);
            return null;
        }
    };

    const handleTimeChange = (time) => {
        try {
            setSelectedTime(time);
            const formattedTime = moment(time).format('HH:mm');
            handleChange({ target: { name, value: formattedTime } });
        } catch (error) {
            console.error('Error handling time change:', error);
        }
    };

    // Disable past times when the selected service date is today so the user
    // can't pick a time that has already passed (which would mark the
    // appointment as "past" and disable the whole form).
    const isFutureTimeForToday = (time) => {
        try {
            const serviceDate = formData?.dateOfService;
            if (!serviceDate) return true;

            const selected = moment(serviceDate);
            if (!selected.isValid()) return true;

            // Only restrict times when the chosen date is the current day.
            if (!selected.isSame(moment(), 'day')) return true;

            const now = moment();
            const slotMinutes = time.getHours() * 60 + time.getMinutes();
            const nowMinutes = now.hours() * 60 + now.minutes();
            return slotMinutes >= nowMinutes;
        } catch (error) {
            console.error('Error filtering past times:', error);
            return true;
        }
    };

    const getProviderAvailablity = async (id) => {
        try {
            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                return { providerAvailablity: [], providerDetails: [] };
            }

            // Fetch provider availability from backend API
            const availabilityResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}/availability`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            // Fetch provider basic info from backend API
            const providerResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            let providerAvailablity = [];
            let providerDetails = [];

            if (availabilityResponse.ok) {
                const availabilityResult = await availabilityResponse.json();
                if (availabilityResult.success && availabilityResult.data) {
                    providerAvailablity = Array.isArray(availabilityResult.data) ? availabilityResult.data : [];
                }
            }

            if (providerResponse.ok) {
                const providerResult = await providerResponse.json();
                if (providerResult.success && providerResult.data) {
                    providerDetails = Array.isArray(providerResult.data) ? providerResult.data : [providerResult.data];
                }
            }

            return { providerAvailablity, providerDetails };
        } catch (error) {
            console.error('Error fetching provider availability:', error);
            return { providerAvailablity: [], providerDetails: [] };
        }
    };

    const getOooByProviderId = async (providerId) => {
        try {
            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                return [];
            }

            // Fetch provider OOO from backend API
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/provider-ooo`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ provider_id: providerId })
                }
            );

            if (!response.ok) {
                return [];
            }

            const result = await response.json();
            if (result.success && result.data) {
                return Array.isArray(result.data) ? result.data : [];
            }

            return [];
        } catch (error) {
            console.error('Error fetching provider OOO:', error);
            return [];
        }
    };

    const isDayAvailable = (date) => {
        if (!availability) return false;
        
        // Check if the date falls within any out-of-office period
        if (oooDates && oooDates.length > 0) {
            const checkDate = dayjs(date);
            const isOutOfOffice = oooDates.some((ooo) => {
                const oooStart = dayjs(ooo.date_start);
                const oooEnd = dayjs(ooo.date_end);
                // Check if the selected date falls within the out-of-office period
                return checkDate.isBetween(oooStart, oooEnd, 'day', '[]');
            });

            if (isOutOfOffice) {
                return false; // Date is not available because provider is out of office
            }
        }

        const dayName = WEEK_DAYS[date.getDay()];
        const filteredSlots = filterCurrentDateTimeSlots(date, availability[dayName]);

        return filteredSlots.length > 0;
    };

    const filterAvailableTimessss = (time) => {
        try {
            let daySelected = localStorage.getItem('selectedDay');
            if (!daySelected) return false;

            const day = moment(daySelected).day();
            const hour = moment(time).hour(); // Use `time` from the picker

            return availability?.[day]?.some((availableHour) => parseInt(availableHour) === hour) ?? false;
        } catch (error) {
            console.error('Error filtering available times:', error);
            return false;
        }
    };
    const filterCurrentDateTimeSlots = (date, slots) => {
        let selectedSlots = slots;
        const currentTime = moment().format('HH:mm');
        const isToday = dayjs().isSame(date, 'day');
        if (isToday) {
            selectedSlots = selectedSlots?.filter((slot) => {
                return slot >= currentTime;
            });
            return selectedSlots;
        } else {
            return slots;
        }
    };
    
    const filterAvailableTimes = (time) => {
        try {
            const daySelected = localStorage.getItem('selectedDay');
            if (!daySelected || !availability) return false;

            const dayName = WEEK_DAYS[moment(daySelected).day()]; // ← Fix here
            const availableSlots = availability[dayName] || [];
            const filteredSlots = filterCurrentDateTimeSlots(formData?.dateOfService, availableSlots);

            const timeStr = moment(time).format('HH:mm');
            return filteredSlots.includes(timeStr) && !bookedSlots.includes(timeStr);
        } catch (error) {
            console.error('Error filtering available times:', error);
            return false;
        }
    };
    const getStatusColor = (status) => {
        const statusKey = status?.toLowerCase();

        // Fo cancellation type, just use 'cancelled' for display
        if (statusKey === 'cancelled_patient' || statusKey === 'cancelled_provider') {
            return statusColors['cancelled'];
        }

        return statusColors[statusKey] || '#333'; // Default color if status is not found
    };
    return (
        <>
            {!noLabel && <FormInputLabel label={label} subText={subText} labelSize={labelSize} required={required} style={labelStyle} />}
            {subtext && <Subtext>{subtext}</Subtext>}

            {isTextFeild && (
                <Grid
                    item
                    xs={textSize}
                    sx={{
                        marginBottom: style?.marginBottom ? style?.marginBottom : '16px',
                        width: '100%',
                        maxWidth: '100%',
                    }}
                >
                    {type === 'date' ? (
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <StyledDatePicker
                                selected={getSafeDateValue(value)}
                                disabled={disabled}
                                onChange={handleDateChange}
                                dateFormat="MMMM d, yyyy"
                                placeholderText="Select a Date"
                                className="border rounded p-2 w-full"
                                required={required}
                                minDate={startDate}
                                maxDate={endDate}
                                popperClassName="custom-datepicker-popper"
                                popperPlacement="bottom-start"
                                popperProps={DATE_PICKER_POPPER_PROPS}
                                showMonthDropdown
                                showYearDropdown
                                scrollableMonthDropdown
                                scrollableYearDropdown
                                yearDropdownItemNumber={100}
                                onKeyDown={(e) => e.preventDefault()}
                                onFocus={(e) => e.target.blur()}
                                style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
                            />
                            {!disabled && (
                                <CalendarToday
                                    style={{
                                        position: 'absolute',
                                        right: '14px',
                                        color: '#757575',
                                        pointerEvents: 'none',
                                        fontSize: '18px',
                                    }}
                                />
                            )}
                        </div>
                    ) : type === 'date' && name === 'dob' ? (
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <StyledDatePicker
                                selected={getSafeDateValue(value)}
                                disabled={disabled}
                                onChange={handleDateChange}
                                dateFormat="MMMM d, yyyy"
                                placeholderText="Select a Date"
                                className="border rounded p-2 w-full"
                                required={required}
                                minDate={startDate}
                                maxDate={endDate || new Date()}
                                popperClassName="custom-datepicker-popper"
                                popperPlacement="bottom-start"
                                popperProps={DATE_PICKER_POPPER_PROPS}
                                showMonthDropdown
                                showYearDropdown
                                scrollableMonthDropdown
                                scrollableYearDropdown
                                yearDropdownItemNumber={100}
                                onKeyDown={(e) => e.preventDefault()}
                                onFocus={(e) => e.target.blur()}
                                style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
                            />
                            {!disabled && (
                                <CalendarToday
                                    style={{
                                        position: 'absolute',
                                        right: '14px',
                                        color: '#757575',
                                        pointerEvents: 'none',
                                        fontSize: '18px',
                                    }}
                                />
                            )}
                        </div>
                    ) : type === 'time' ? (
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <StyledDatePicker
                                selected={selectedTime || (value ? new Date(`1970-01-01T${value}`) : null)}
                                disabled={disabled}
                                onChange={handleTimeChange}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={slotsGap}
                                filterTime={isFutureTimeForToday}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                                placeholderText="Select a Time"
                                className="border rounded p-2 w-full"
                                style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
                                onKeyDown={readOnly ? (e) => e.preventDefault() : undefined}
                            />
                            {!disabled && (
                                <AccessTime
                                    style={{
                                        position: 'absolute',
                                        right: '14px',
                                        color: '#757575',
                                        pointerEvents: 'none',
                                        fontSize: '18px',
                                    }}
                                />
                            )}
                        </div>
                    ) : (
                        <>
                            {helperText && (
                                <span className={'help-text'} style={{marginBottom: '12px', display: 'block'}}>
                                    {helperText}
                                </span>
                            )}

                            <TextField
                                fullWidth
                                name={name}
                                value={
                                    name === 'appointmentStatus' &&
                                    (value === 'cancelled_provider' || value === 'cancelled_patient')
                                        ? 'cancelled'
                                        : value !== null && value !== undefined
                                          ? value
                                      : formData
                                        ? formData[name]
                                        : ''
                            }
                            onChange={(e) => {
                                handleChange(e);
                            }}
                            required={required}
                            placeholder={placeholder}
                            type={type}
                            InputProps={InputProps}
                            disabled={disabled}
                            inputProps={{
                                min: min,
                                'aria-required': required,
                                'aria-invalid': !!error,
                                ...inputProps,
                            }}
                            id={id}
                            helperText={error}
                            sx={{
                                '& .MuiOutlinedInput-input::placeholder': {
                                    color: '#4A4A4A',
                                    opacity: 1,
                                },
                                // Ensure disabled fields don't show hover styles on the outline
                                '& .MuiOutlinedInput-root.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#cbc1b7 !important',
                                },
                                '& .MuiOutlinedInput-root.Mui-disabled:hover': {
                                    backgroundColor: '#f5f1ed !important',
                                    boxShadow: 'none !important',
                                },
                                '& .MuiOutlinedInput-root.Mui-disabled': {
                                    backgroundColor: '#f5f1ed !important',
                                },
                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#cbc1b7 !important',
                                },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    transition: disabled ? 'none' : 'all 0.3s ease',
                                    backgroundColor: '#f5f1ed',
                                    border: '1px solid #cbc1b7',
                                    ...(disabled && {
                                        opacity: 0.6,
                                        cursor: 'not-allowed',
                                        backgroundColor: '#f5f1ed',
                                        borderColor: '#cbc1b7',
                                        pointerEvents: 'none',
                                    }),
                                    ...(error && {
                                        border: '1px solid #dc3545',
                                    }),
                                    ...(disabled
                                        ? {
                                            cursor: 'not-allowed',
                                        }
                                        : {
                                            '&:hover': {
                                                borderColor: '#ff5f39',
                                                backgroundColor: 'white',
                                            },
                                        }),
                                    '&.Mui-focused': {
                                        borderColor: '#ff5f39',
                                        backgroundColor: 'white',
                                        boxShadow: '0 0 0 2px rgba(255, 95, 57, 0.1)',
                                    },
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                                '& .MuiFormHelperText-root': {
                                    color: 'red',
                                },
                                '& .MuiInputBase-input': {
                                    color: disabled
                                        ? '#6B7280'
                                        : name === 'appointmentStatus' || name === 'paymentStatus'
                                          ? getStatusColor(value)
                                          : '#000000',
                                    paddingRight: '14px !important',
                                    ...(disabled && { cursor: 'not-allowed' }),
                                },
                                '& .Mui-disabled': {
                                    color: '#6B7280 !important',
                                    WebkitTextFillColor: '#6B7280 !important',
                                    cursor: 'not-allowed',
                                },
                            }}
                            inputMode={inputMode}
                            onInput={onInput}
                            title={title}
                            />
                        </>
                    )}

                    {additionAction && (
                        <div style={{marginTop: '8px'}}>
                            {additionAction}
                        </div>
                    )}
                </Grid>
            )}
        </>
    );
}
